import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import {
  WAFClearanceDetail,
  WAFClearanceResolveState,
  WAFTelemetryOutcomeExtra,
  type WAFTerminalFailureOutcome,
  type WAFWidgetFailureDetail,
} from '@/services/waf/types';
import { WAFBlockingDialog } from '@/UI/WAFFailureModal/WAFBlockingDialog';

interface WAFFailureModalStandaloneProps {
  outcome: WAFTerminalFailureOutcome;
  detail?: WAFClearanceDetail;
  onReload: () => void;
  isEmbedded?: boolean;
}

// The widget-failure details all mean "your browser or an extension is blocking the challenge", so all get
// the browser-help link, not just the unsupported-browser one. Typed against WAFWidgetFailureDetail so adding a
// fourth widget detail to that union fails the build here until it is classified, rather than silently dropping
// the link for it.
const WIDGET_FAILURE_DETAIL_FLAGS: Record<WAFWidgetFailureDetail, true> = {
  [WAFClearanceDetail.WidgetUnsupportedInBrowser]: true,
  [WAFClearanceDetail.WidgetUnavailable]: true,
  [WAFClearanceDetail.WidgetRenderCrash]: true,
};
const WIDGET_FAILURE_DETAILS: ReadonlySet<WAFClearanceDetail> = new Set(
  Object.keys(WIDGET_FAILURE_DETAIL_FLAGS) as WAFWidgetFailureDetail[],
);

// isLikelyBrowserInterference marks the outcomes whose failure is commonly a browser/extension problem (exhausted
// replays), not a provider Error/Unavailable. It is one input to showBrowserHelp below
// (embed and the widget-failure details are the others), not the final decision. Exhaustive so a new outcome
// must classify itself.
const FAILURE_COPY_BY_OUTCOME: Record<
  WAFTerminalFailureOutcome,
  { titleKey: string; bodyKey: string; isLikelyBrowserInterference: boolean }
> = {
  [WAFClearanceResolveState.Error]: {
    titleKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_ERROR_TITLE,
    bodyKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_ERROR_BODY,
    isLikelyBrowserInterference: false,
  },
  [WAFClearanceResolveState.Unavailable]: {
    titleKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_UNAVAILABLE_TITLE,
    bodyKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_UNAVAILABLE_BODY,
    isLikelyBrowserInterference: false,
  },
  [WAFTelemetryOutcomeExtra.ReplaysExhausted]: {
    titleKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_EXHAUSTED_TITLE,
    bodyKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_EXHAUSTED_BODY,
    isLikelyBrowserInterference: true,
  },
  [WAFTelemetryOutcomeExtra.RecoveryCircuitOpen]: {
    titleKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_CIRCUIT_OPEN_TITLE,
    bodyKey: I18N_KEYS.SECURITY_CHALLENGE.FAILURE_CIRCUIT_OPEN_BODY,
    isLikelyBrowserInterference: false,
  },
};

export function WAFFailureModalStandalone({
  outcome,
  detail,
  onReload,
  isEmbedded = false,
}: WAFFailureModalStandaloneProps) {
  const { t } = useTranslation();
  // hasOwn (not `?? fallback`) so an inherited Object.prototype key ('constructor', 'toString') passed as a
  // runtime out-of-union outcome falls back to Error copy rather than resolving a truthy inherited value.
  const copy = Object.hasOwn(FAILURE_COPY_BY_OUTCOME, outcome)
    ? FAILURE_COPY_BY_OUTCOME[outcome]
    : FAILURE_COPY_BY_OUTCOME[WAFClearanceResolveState.Error];
  const showBrowserHelp =
    isEmbedded || copy.isLikelyBrowserInterference || (detail !== undefined && WIDGET_FAILURE_DETAILS.has(detail));

  return (
    <WAFBlockingDialog
      title={t(copy.titleKey)}
      body={t(copy.bodyKey)}
      showBrowserHelp={showBrowserHelp}
      onReload={onReload}
      isEmbedded={isEmbedded}
    />
  );
}
